import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ClipboardList,
} from "lucide-react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../../styles/ag-grid-custom.css";
import { ColDef, ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { toast } from "react-toastify";
import { formatDate } from "../../../IncidentManagement/utils/utils";
import {
  ICSIncident,
  ICSRole,
  ICSDecisionLog,
  ICSRoleType,
} from "../types/types";
import {
  assignICSRole,
  removeICSRole,
  createDecisionLog,
  getDecisionLogs,
  approveDecision,
} from "../services/service";
import { getIncidentById } from "../../../IncidentManagement/services/service";
import type { Incident } from "../../../IncidentManagement/types/types";
import { useOrgUsersStore } from "../../../../store/store";
import { usePermissions } from "../../../../hooks/usePermission";
import { EXTERNAL_EMERGENCY_PREPAREDNESS_ICS } from "../../../../constants/modules";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

interface ICSIncidentDetailsProps {
  incident: ICSIncident;
  onClose: () => void;
}

const ICSIncidentDetails: React.FC<ICSIncidentDetailsProps> = ({
  incident,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<
    "roles" | "decisions" | "incident"
  >("incident");
  const [roles, setRoles] = useState<ICSRole[]>(incident.roles || []);
  const [decisionLogs, setDecisionLogs] = useState<ICSDecisionLog[]>(
    incident.decisionLogs || [],
  );
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState<ICSRoleType | "">(
    "",
  );
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [decisionTitle, setDecisionTitle] = useState("");
  const [decisionDescription, setDecisionDescription] = useState("");
  const [decisionType, setDecisionType] = useState("");
  const [priority, setPriority] = useState<
    "Low" | "Medium" | "High" | "Critical"
  >("Medium");
  const [relatedRoles, setRelatedRoles] = useState<ICSRoleType[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectingDecisionId, setRejectingDecisionId] = useState<string | null>(
    null,
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [sourceIncident, setSourceIncident] = useState<Incident | null>(null);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [incidentError, setIncidentError] = useState<string | null>(null);

  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { canUpdate } = usePermissions();
  const hasUpdatePerm = canUpdate(EXTERNAL_EMERGENCY_PREPAREDNESS_ICS);

  const icsRoleTypes: ICSRoleType[] = [
    "Incident Commander",
    "Operations Chief",
    "Safety Officer",
    "Communications Officer",
    "Planning Chief",
    "Logistics Chief",
    "Finance Chief",
  ];

  useEffect(() => {
    fetchOrgUsers();
    if (activeTab === "decisions") {
      loadDecisionLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Switch away from Decision tab if all roles are removed
  useEffect(() => {
    if (activeTab === "decisions" && roles.length === 0) {
      setActiveTab("incident");
    }
  }, [activeTab, roles.length]);

  // Fetch source incident details by incidentId
  useEffect(() => {
    if (!incident?.incidentId) {
      setSourceIncident(null);
      setIncidentError(null);
      return;
    }
    let cancelled = false;
    const loadSourceIncident = async () => {
      setIncidentLoading(true);
      setIncidentError(null);
      try {
        const response = await getIncidentById(incident.incidentId);
        if (!cancelled && response.success && response.data) {
          setSourceIncident(response.data);
        } else if (!cancelled && !response.success) {
          setSourceIncident(null);
          setIncidentError("Failed to load incident details");
        }
      } catch (err) {
        if (!cancelled) {
          setSourceIncident(null);
          setIncidentError("Incident not found or no longer available");
        }
      } finally {
        if (!cancelled) setIncidentLoading(false);
      }
    };
    loadSourceIncident();
    return () => {
      cancelled = true;
    };
  }, [incident?.incidentId]);

  const loadDecisionLogs = async () => {
    try {
      const response = await getDecisionLogs(incident.id);
      if (response.success) {
        setDecisionLogs(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load decision logs:", error);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedRoleType || !selectedUserId) {
      toast.error("Please select both role type and user");
      return;
    }

    if (roles.some((r) => r.roleType === selectedRoleType)) {
      toast.error("This role type is already assigned");
      return;
    }

    try {
      setSubmitting(true);
      const response = await assignICSRole(incident.id, {
        roleType: selectedRoleType,
        assignedUserId: selectedUserId,
      });

      if (response.success) {
        setRoles([...roles, response.data]);
        setSelectedRoleType("");
        setSelectedUserId("");
        setShowRoleForm(false);
        toast.success("Role assigned successfully");
      }
    } catch (error) {
      toast.error("Failed to assign role");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    try {
      await removeICSRole(incident.id, roleId);
      setRoles(roles.filter((r) => r.id !== roleId));
      toast.success("Role removed successfully");
    } catch (error) {
      toast.error("Failed to remove role");
    }
  };

  const handleCreateDecision = async () => {
    if (!decisionTitle || !decisionDescription) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      const response = await createDecisionLog(incident.id, {
        incidentId: incident.incidentId,
        decisionTitle,
        decisionDescription,
        decisionType,
        priority,
        relatedRoles,
        attachments: [],
      });

      if (response.success) {
        setDecisionLogs([...decisionLogs, response.data]);
        setDecisionTitle("");
        setDecisionDescription("");
        setDecisionType("");
        setPriority("Medium");
        setRelatedRoles([]);
        setShowDecisionForm(false);
        toast.success("Decision log created successfully");
      }
    } catch (error) {
      toast.error("Failed to create decision log");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveDecision = async (
    decisionId: string,
    status: "Approved" | "Rejected",
    rejectionReason?: string,
  ) => {
    try {
      setSubmitting(true);
      const response = await approveDecision(incident.id, decisionId, {
        decisionLogId: decisionId,
        status,
        rejectionReason,
      });

      if (response.success) {
        await loadDecisionLogs();
        toast.success(`Decision ${status.toLowerCase()} successfully`);
        if (status === "Rejected") {
          setShowRejectionModal(false);
          setRejectionReason("");
          setRejectingDecisionId(null);
        }
      }
    } catch (error) {
      toast.error(`Failed to ${status.toLowerCase()} decision`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectClick = (decisionId: string) => {
    setRejectingDecisionId(decisionId);
    setShowRejectionModal(true);
    setRejectionReason("");
  };

  const handleConfirmRejection = () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    if (rejectingDecisionId) {
      handleApproveDecision(rejectingDecisionId, "Rejected", rejectionReason);
    }
  };

  interface UserOption {
    id: string;
    name: string;
  }

  const userOptions: UserOption[] = orgUsersData.map(
    (user: any): UserOption => ({
      id: user._id || user.id,
      name: `${user.name || user.userName || ""} - ${user.email || ""}`,
    }),
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "Rejected":
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const roleColumnDefs = useMemo<ColDef<ICSRole>[]>(
    () => [
      {
        headerName: "Role Type",
        field: "roleType",
        flex: 1,
        minWidth: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        headerName: "Assigned To",
        flex: 1,
        minWidth: 180,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params) => {
          const role = params.data;
          if (!role) return "—";
          if (role.assignedUserName) return role.assignedUserName;
          const userOptionsList = params.context?.userOptions as
            | UserOption[]
            | undefined;
          const user = userOptionsList?.find(
            (u) => u.id === role.assignedUserId,
          );
          return user?.name ?? "—";
        },
      },
      {
        headerName: "Assigned At",
        field: "assignedAt",
        width: 130,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Status",
        field: "status",
        width: 110,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        headerName: "Actions",
        width: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const role = params.data;
          if (!role) return null;
          const onRemove = params.context?.onRemove;
          const userHasUpdatePerm = params.context?.hasUpdatePerm;
          return (
            <div className="flex gap-2 h-full items-center">
              {userHasUpdatePerm && (
                <button
                  type="button"
                  onClick={() => onRemove?.(role.id)}
                  className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  const decisionColumnDefs = useMemo<ColDef<ICSDecisionLog>[]>(
    () => [
      {
        headerName: "Title",
        field: "decisionTitle",
        flex: 1,
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        headerName: "Description",
        field: "decisionDescription",
        flex: 2,
        minWidth: 180,
        headerStyle: { backgroundColor: "#e9f5ff" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Type",
        field: "decisionType",
        width: 130,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueFormatter: (params) => params.value ?? "—",
      },
      {
        headerName: "Priority",
        field: "priority",
        width: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        headerName: "Status",
        field: "status",
        width: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => {
          const status = params.value;
          const icon = params.context?.getStatusIcon?.(status) ?? (
            <span className="text-sm">{status}</span>
          );
          return (
            <div className="flex items-center gap-2">
              {icon}
              <span className="text-sm font-medium">{status}</span>
            </div>
          );
        },
      },
      {
        headerName: "Proposed By",
        field: "proposedByName",
        width: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueFormatter: (params) => params.value ?? "—",
      },
      {
        headerName: "Proposed At",
        field: "proposedAt",
        width: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Approved/Rejected By",
        width: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueFormatter: (params) => {
          const d = params.data;
          if (!d?.approvedByName) return "—";
          return d.approvedByName;
        },
      },
      {
        headerName: "Approved At",
        field: "approvedAt",
        width: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueFormatter: (params) =>
          params.value ? formatDate(params.value) : "—",
      },
      {
        headerName: "Actions",
        width: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const decision = params.data;
          if (!decision || decision.status !== "Pending") return null;
          const onApprove = params.context?.onApprove;
          const onRejectClick = params.context?.onRejectClick;
          const userHasUpdatePerm = params.context?.hasUpdatePerm;
          return (
            <div className="flex gap-2 h-full items-center py-2">
              <button
                type="button"
                onClick={() => onApprove?.(decision.id)}
                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!userHasUpdatePerm}
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => onRejectClick?.(decision.id)}
                className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!userHasUpdatePerm}
              >
                Reject
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="fixed inset-0 z-[1000001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              ICS Incident Operations
            </h2>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-sm font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                Level {incident.status}
              </span>
              <span className="text-sm text-gray-500">
                Created {formatDate(incident.activatedAt)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-200 px-6 bg-white gap-8">
          <button
            onClick={() => setActiveTab("incident")}
            className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "incident"
                ? "border-teal-600 text-teal-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Incident Details
            </div>
          </button>
          <button
            onClick={() => setActiveTab("roles")}
            className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "roles"
                ? "border-teal-600 text-teal-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assigned Roles ({roles.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab("decisions")}
            className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "decisions"
                ? "border-teal-600 text-teal-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            } ${roles.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={roles.length === 0}
            title={
              roles.length === 0
                ? "Assign at least one role to access Decision Logs"
                : undefined
            }
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Decision Logs ({decisionLogs.length})
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "incident" && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Source Incident
              </h3>
              {incidentLoading && (
                <div className="py-8 text-center text-gray-500">
                  Loading incident details...
                </div>
              )}
              {incidentError && !incidentLoading && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {incidentError}
                </div>
              )}
              {sourceIncident && !incidentLoading && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Incident Number
                      </p>
                      <p className="mt-1 font-medium text-gray-900">
                        {sourceIncident.incidentNumber ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Department
                      </p>
                      <p className="mt-1 font-medium text-gray-900">
                        {sourceIncident.department ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Severity
                      </p>
                      <p className="mt-1 font-medium text-gray-900">
                        {sourceIncident.severity ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Status
                      </p>
                      <p className="mt-1 font-medium text-gray-900">
                        {sourceIncident.status ?? "—"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Place of Occurrence
                      </p>
                      <p className="mt-1 font-medium text-gray-900">
                        {sourceIncident.placeOfOccurrence ?? "—"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Description / What Went Wrong
                      </p>
                      <p className="mt-1 font-medium text-gray-900 whitespace-pre-wrap">
                        {sourceIncident.whatWentWrong ?? "—"}
                      </p>
                    </div>

                    {(sourceIncident.patientName ||
                      sourceIncident.patientId ||
                      sourceIncident.physicianHod) && (
                      <div className="md:col-span-2 mt-2 p-3 bg-gray-50 rounded border border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          Patient Information
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-900">
                          {sourceIncident.patientName && (
                            <span>Patient: {sourceIncident.patientName}</span>
                          )}
                          {sourceIncident.patientId && (
                            <span>ID: {sourceIncident.patientId}</span>
                          )}
                          {sourceIncident.physicianHod && (
                            <span>
                              Physician/HOD: {sourceIncident.physicianHod}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "roles" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Assigned Roles</h3>
                {hasUpdatePerm && (
                  <button
                    onClick={() => setShowRoleForm(true)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700"
                  >
                    Assign Role
                  </button>
                )}
              </div>

              <div className="roles-grid-wrapper min-h-[200px]">
                {roles.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No roles assigned yet
                  </p>
                ) : (
                  <div
                    className="ag-theme-alpine"
                    style={{ height: "100%", minHeight: 280 }}
                  >
                    <AgGridReact<ICSRole>
                      rowData={roles}
                      columnDefs={roleColumnDefs}
                      context={{
                        onRemove: handleRemoveRole,
                        userOptions,
                        hasUpdatePerm,
                      }}
                      rowHeight={48}
                      headerHeight={44}
                      domLayout="autoHeight"
                      defaultColDef={{
                        sortable: true,
                        resizable: true,
                      }}
                      getRowId={(params) => params.data.id}
                      suppressCellFocus={true}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "decisions" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Decisions</h3>
                {hasUpdatePerm && (
                  <button
                    onClick={() => setShowDecisionForm(true)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700"
                  >
                    Create Decision
                  </button>
                )}
              </div>

              <div className="decisions-grid-wrapper min-h-[200px]">
                {decisionLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No decisions recorded yet
                  </p>
                ) : (
                  <div
                    className="ag-theme-alpine"
                    style={{ height: "100%", minHeight: 300 }}
                  >
                    <AgGridReact<ICSDecisionLog>
                      rowData={decisionLogs}
                      columnDefs={decisionColumnDefs}
                      context={{
                        getStatusIcon,
                        onApprove: (id: string) =>
                          handleApproveDecision(id, "Approved"),
                        onRejectClick: handleRejectClick,
                        hasUpdatePerm,
                      }}
                      rowHeight={56}
                      headerHeight={44}
                      domLayout="autoHeight"
                      defaultColDef={{
                        sortable: true,
                        resizable: true,
                      }}
                      getRowId={(params) => params.data.id}
                      suppressCellFocus={true}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Role Assignment Modal */}
      {showRoleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">
              Assign ICS Role
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Type
                </label>
                <select
                  value={selectedRoleType}
                  onChange={(e) =>
                    setSelectedRoleType(e.target.value as ICSRoleType)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select Role</option>
                  {icsRoleTypes
                    .filter((role) => !roles.some((r) => r.roleType === role))
                    .map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select User</option>
                  {userOptions.map((user: UserOption) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRoleForm(false);
                    setSelectedRoleType("");
                    setSelectedUserId("");
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAssignRole}
                  disabled={!selectedRoleType || !selectedUserId || submitting}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:opacity-50"
                >
                  {submitting ? "Assigning..." : "Assign Role"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Decision Modal */}
      {showDecisionForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4 text-gray-900">
              Create Decision Log
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={decisionTitle}
                  onChange={(e) => setDecisionTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter decision title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={decisionDescription}
                  onChange={(e) => setDecisionDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={4}
                  placeholder="Enter decision description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Decision Type
                  </label>
                  <input
                    type="text"
                    value={decisionType}
                    onChange={(e) => setDecisionType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g., Resource Allocation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) =>
                      setPriority(
                        e.target.value as
                          | "Low"
                          | "Medium"
                          | "High"
                          | "Critical",
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDecisionForm(false);
                    setDecisionTitle("");
                    setDecisionDescription("");
                    setDecisionType("");
                    setPriority("Medium");
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateDecision}
                  disabled={
                    !decisionTitle || !decisionDescription || submitting
                  }
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:opacity-50"
                >
                  {submitting ? "Creating..." : "Create Decision"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Reject Decision
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this decision:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectionReason("");
                  setRejectingDecisionId(null);
                }}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRejection}
                disabled={
                  submitting || !rejectionReason.trim() || !hasUpdatePerm
                }
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Rejecting..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICSIncidentDetails;
